import { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UploadCloud, FileText, Camera } from "lucide-react";

export default function UploadDialog({ open, onOpenChange, workerId, item, onUploaded }) {
  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [completedDate, setCompletedDate] = useState("");
  const [citizenship, setCitizenship] = useState("uk");
  const [shareCode, setShareCode] = useState("");
  const [dob, setDob] = useState("");
  const [visaExpiry, setVisaExpiry] = useState("");
  const [dbsCertNumber, setDbsCertNumber] = useState("");
  const [dbsDob, setDbsDob] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const reset = () => {
    setFile(null);
    setExpiryDate("");
    setIssueDate("");
    setCompletedDate("");
    setCitizenship("uk");
    setShareCode("");
    setDob("");
    setVisaExpiry("");
    setDbsCertNumber("");
    setDbsDob("");
    setBusy(false);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  if (!item) return null;
  const isRTW = item.special_type === "right_to_work";
  const isDBS = item.special_type === "dbs";
  const isIdPhoto = item.special_type === "id_photo";
  const dateField = item.date_field;
  const expiryYears = item.expiry_years || 0;
  const wantsFile = (!isRTW || citizenship === "uk") || isDBS;
  const nonUkRTW = isRTW && citizenship === "non_uk";

  const validate = () => {
    if (wantsFile && !file) return "Please attach a file";
    if (isDBS) {
      if (!dbsCertNumber.trim()) return "DBS certificate number is required";
      if (!dbsDob) return "Date of birth is required";
    }
    if (dateField === "expiry_date" && !expiryDate) return "Expiry date is required";
    if (dateField === "issue_date" && !issueDate) return "Issue date is required";
    if (dateField === "completed_date" && !completedDate) return "Completion date is required";
    if (nonUkRTW) {
      if (!shareCode.trim()) return "Share code is required";
      if (!dob) return "Date of birth is required";
      if (!visaExpiry) return "Visa expiry date is required";
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("item_key", item.key);
      if (file) form.append("file", file);
      if (dateField === "expiry_date") form.append("expiry_date", expiryDate);
      if (dateField === "issue_date") form.append("issue_date", issueDate);
      if (dateField === "completed_date") form.append("completed_date", completedDate);
      if (isRTW) {
        const meta = { citizenship };
        if (nonUkRTW) {
          meta.share_code = shareCode.trim();
          meta.dob = dob;
          meta.visa_expiry = visaExpiry;
        }
        form.append("metadata_json", JSON.stringify(meta));
      }
      if (isDBS) {
        form.append("metadata_json", JSON.stringify({
          certificate_number: dbsCertNumber.trim(),
          dob: dbsDob,
        }));
      }
      await api.post(`/workers/${workerId}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Submitted for review", { description: item.title });
      onOpenChange(false);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error("Upload failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Upload document</DialogTitle>
          <DialogDescription>
            {item.title}
            {expiryYears > 0 && dateField === "completed_date" && (
              <span className="ml-1 text-slate-500">· expiry auto-calculated as {expiryYears} year{expiryYears > 1 ? "s" : ""} from date completed</span>
            )}
            {expiryYears > 0 && dateField === "issue_date" && (
              <span className="ml-1 text-slate-500">· expires {expiryYears} year{expiryYears > 1 ? "s" : ""} from issue date</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isRTW && (
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: "uk", label: "UK National" },
              { k: "non_uk", label: "Non-UK" },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setCitizenship(opt.k)}
                data-testid={`rtw-${opt.k}`}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                  citizenship === opt.k
                    ? "border-teal-700 bg-teal-50 text-teal-800 ring-2 ring-teal-200"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {nonUkRTW && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="label-caps">Visa share code</Label>
              <Input value={shareCode} onChange={(e) => setShareCode(e.target.value)} data-testid="rtw-share-code" placeholder="e.g. W88 ABC 123" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-caps">Date of birth</Label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="rtw-dob" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-caps">Visa end date</Label>
              <Input type="date" value={visaExpiry} onChange={(e) => setVisaExpiry(e.target.value)} data-testid="rtw-visa-expiry" />
            </div>
          </div>
        )}

        {isDBS && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="label-caps">DBS certificate number</Label>
              <Input
                value={dbsCertNumber}
                onChange={(e) => setDbsCertNumber(e.target.value)}
                data-testid="dbs-cert-number-input"
                placeholder="e.g. 001234567890"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="label-caps">Date of birth</Label>
              <Input
                type="date"
                value={dbsDob}
                onChange={(e) => setDbsDob(e.target.value)}
                data-testid="dbs-dob-input"
              />
            </div>
            <div className="sm:col-span-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
              Your surname will be attached from your profile so a manager can verify you on the
              <a href="https://secure.crbonline.gov.uk/enquiry/enquirySearch.do" target="_blank" rel="noreferrer" className="text-teal-700 hover:underline mx-1">DBS Update Service</a>.
            </div>
          </div>
        )}

        {dateField === "expiry_date" && (
          <div className="space-y-1.5">
            <Label className="label-caps">Expiry date on document</Label>
            <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} data-testid="upload-expiry-date" />
          </div>
        )}
        {dateField === "issue_date" && (
          <div className="space-y-1.5">
            <Label className="label-caps">Issue date (expires {expiryYears}yr later)</Label>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="upload-issue-date" />
          </div>
        )}
        {dateField === "completed_date" && (
          <div className="space-y-1.5">
            <Label className="label-caps">Date completed (expires {expiryYears}yr later)</Label>
            <Input type="date" value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} data-testid="upload-completed-date" />
          </div>
        )}

        {wantsFile && (
          <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-5 text-center bg-stone-50">
            {file ? (
              isIdPhoto && file.type?.startsWith("image/") ? (
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Selfie preview"
                    className="h-32 w-32 rounded-full object-cover border border-slate-200"
                    data-testid="id-photo-preview"
                  />
                  <span className="text-xs text-slate-500 truncate max-w-[16rem]">{file.name}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-slate-700">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm truncate max-w-[16rem]">{file.name}</span>
                </div>
              )
            ) : isIdPhoto ? (
              <>
                <Camera className="mx-auto h-9 w-9 text-slate-400" />
                <p className="mt-2 text-sm text-slate-600">Snap a head-and-shoulders photo — plain background, clear face.</p>
                <p className="text-xs text-slate-500">JPEG, PNG, WEBP or HEIC · up to 5MB</p>
              </>
            ) : (
              <>
                <UploadCloud className="mx-auto h-9 w-9 text-slate-400" />
                <p className="mt-2 text-sm text-slate-600">PDF, PNG, JPG or DOCX up to 20MB</p>
              </>
            )}
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              data-testid="upload-file-input"
              accept={isIdPhoto ? "image/png,image/jpeg,image/webp,image/heic,image/heif" : ".pdf,.png,.jpg,.jpeg,.doc,.docx,.webp"}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {isIdPhoto && (
              <input
                type="file"
                className="hidden"
                accept="image/*"
                capture="user"
                data-testid="upload-camera-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                id="id-photo-camera"
              />
            )}
            <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="upload-choose-file"
                onClick={() => fileInput.current?.click()}
              >
                {file ? "Change file" : isIdPhoto ? "Choose photo" : "Select file"}
              </Button>
              {isIdPhoto && (
                <Button
                  type="button"
                  size="sm"
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="upload-take-photo"
                  onClick={() => document.getElementById("id-photo-camera")?.click()}
                >
                  <Camera className="h-3.5 w-3.5 mr-1.5" /> Take photo
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="upload-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="upload-submit"
          >
            {busy ? "Uploading…" : "Submit for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
